import { PREVIEW_JOURNEY_ID } from '@epilot/journey-logic-commons/src/schemas/v4'
import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { shallow } from 'zustand/shallow'

import type { AppParams } from '../hooks/useAppParams/types'
import { useAppStoreSelector } from '../hooks/useAppStoreSelector'
import { useDesignLoader } from '../hooks/useDesignLoader'
import {
  getJourneyByIdQueryKey,
  useJourneyQuery
} from '../hooks/useJourneyQuery'
import { useLanguage } from '../hooks/useLanguage'
import { usePreview } from '../hooks/usePreview'
import { useProductAnalytics } from '../hooks/useProductAnalytics'
import { usePwaManifest } from '../hooks/usePwaManifest'
import { useTracking } from '../hooks/useTracking'
import { getJourneyById } from '../services/journey-service'
import { getQueryClient } from '../services/queryClient'
import { appParamsChanged } from '../store/actions/appParamsChanged'
import { journeyHydrated } from '../store/actions/journeyHydrated'
import { setAppError } from '../store/actions/setAppError'
import { setInitialStep } from '../store/actions/setInitialStep'
import {
  selectAppStatus,
  selectHydratedJourneyId,
  selectLaunchableJourneyIdsSorted,
  selectSelectedJourneyId
} from '../store/selectors/config'
import { APP_ERROR, APP_STATUS } from '../store/types/enums'

import { AppError } from './AppError'

/**
 * Responsible for orchestrating the App by running the most foundamental actions.
 */
export const AppManager = (
  props: PropsWithChildren<{
    appParams: AppParams
  }>
) => {
  const appParams = props.appParams
  const appStatus = useAppStoreSelector(selectAppStatus)
  const selectedJourneyId = useAppStoreSelector(selectSelectedJourneyId)
  const linkedJourneyIds = useAppStoreSelector(
    selectLaunchableJourneyIdsSorted,
    shallow
  )
  const hydatedJourneyId = useAppStoreSelector(selectHydratedJourneyId)

  useLanguage(appParams.lang)
  usePreview(appParams.preview)
  usePwaManifest()
  useTracking()
  useProductAnalytics()
  useDesignLoader()

  // sync app params to store
  useEffect(() => appParamsChanged({ appParams }), [appParams])

  const rootQuery = useJourneyQuery(appParams.journeyId, {
    // is not preview and id exists
    enabled: !appParams.preview && !!appParams.journeyId
  })

  useEffect(() => {
    if (!appParams.preview && rootQuery?.data) {
      journeyHydrated({ response: rootQuery.data, diferentiator: 'root' })
    }
  }, [appParams.preview, rootQuery?.data])

  const selectedQuery = useJourneyQuery(selectedJourneyId, {
    enabled:
      // id exists
      !!selectedJourneyId &&
      // is not preview only
      selectedJourneyId !== PREVIEW_JOURNEY_ID &&
      // and is launchable
      linkedJourneyIds.includes(selectedJourneyId)
  })

  useEffect(() => {
    if (selectedQuery?.data) {
      journeyHydrated({
        response: selectedQuery.data,
        diferentiator: 'selectable'
      })
    }
  }, [selectedQuery?.data])

  useEffect(() => {
    if (!hydatedJourneyId) return

    // WARNING: should be kept in sync with the logic in `journeyClose` action
    setInitialStep({
      initialStepIndexOverride: appParams.dataInjectionOptions?.initialStepIndex
    })
    // Should only be called once when the given journey is hydrated,
    // therefore we don't call it again if the injection values get changed.
    // A separate mechanism should be used to handle data injection from message handlers
    // which should also take care of the additional injection capabilities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydatedJourneyId])

  useEffect(() => {
    if (
      appStatus !== APP_STATUS.FAILED &&
      !appParams.journeyId &&
      !appParams.preview
    ) {
      return void setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Journey details are missing and this is not a preview session.'
      })
    }
  }, [appStatus, appParams.journeyId, appParams.preview])

  useEffect(() => {
    for (const id of linkedJourneyIds) {
      getQueryClient().prefetchQuery({
        queryKey: getJourneyByIdQueryKey(id),
        queryFn: (context) => {
          return getJourneyById(id, context.signal)
        }
      })
    }
  }, [linkedJourneyIds])

  useEffect(() => {
    if (appStatus === APP_STATUS.FAILED) return

    if (rootQuery?.isError) {
      setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Journey was not found.'
      })
    } else if (selectedQuery?.isError) {
      setAppError({
        error: APP_ERROR.JOURNEY_NOT_FOUND,
        reason: 'Launchable Journey was not found.'
      })
    }
  }, [appStatus, rootQuery?.isError, selectedQuery?.isError])

  /*
    In case of a fatal App error, renders an error page. Otherwise, renders children and let
    each component determine when its time to render (i.e. waiting for the ready state, showing UI skeletons).
  */
  return appStatus === APP_STATUS.FAILED ? <AppError /> : <>{props.children}</>
}
